/**
 * Ascensions are cumulative challenge rules. Endless is deliberately not
 * represented here; it is a separate run option that can compose with any
 * Ascension level.
 */
export const ASCENSION_ICON = '✦';

// Every Ascension level scales enemy HP AND damage by +20% (cumulative,
// additive), so Ascension X caps the run at 3x enemy stats. This universal
// stat climb is derived purely from the level below (createAscensionRules) —
// it is no longer tied to the per-level `modifiers` tags, which now only carry
// each level's unique flavor twist.
export const ASCENSION_STAT_STEP = 0.2;
export const ASCENSION_STAT_CAP = 3;

export function getAscensionStatMultiplier(level = 0) {
  return Math.min(ASCENSION_STAT_CAP, 1 + ASCENSION_STAT_STEP * Math.max(0, level));
}

export const ASCENSIONS = [
  { level: 0, name: 'Normal', description: 'The standard gauntlet.', modifiers: [] },
  { level: 1, name: 'Ascension I', description: 'Every foe is tougher and hits harder.', modifiers: ['enemyDamage'] },
  { level: 2, name: 'Ascension II', description: 'Bosses no longer heal between stages.', modifiers: ['enemyDamage', 'noBossHealing'] },
  { level: 3, name: 'Ascension III', description: 'Every wave has twice as many minions.', modifiers: ['enemyDamage', 'noBossHealing', 'reinforcedWaves'] },
  { level: 4, name: 'Ascension IV', description: 'Level-ups offer one fewer choice.', modifiers: ['enemyDamage', 'noBossHealing', 'reinforcedWaves', 'fewerChoices'] },
  { level: 5, name: 'Ascension V', description: 'Health pods are half as effective.', modifiers: ['enemyDamage', 'noBossHealing', 'reinforcedWaves', 'fewerChoices', 'reducedPickupHealing'] },
  { level: 6, name: 'Ascension VI', description: 'Bosses enrage after 90 seconds.', modifiers: ['enemyDamage', 'noBossHealing', 'reinforcedWaves', 'fewerChoices', 'reducedPickupHealing', 'bossEnrage'] },
  { level: 7, name: 'Ascension VII', description: 'Your maximum HP is reduced by 20%.', modifiers: ['enemyDamage', 'noBossHealing', 'reinforcedWaves', 'fewerChoices', 'reducedPickupHealing', 'bossEnrage', 'frailty'] },
  { level: 8, name: 'Ascension VIII', description: 'A damaging arena pulse occurs every 12 seconds.', modifiers: ['enemyDamage', 'noBossHealing', 'reinforcedWaves', 'fewerChoices', 'reducedPickupHealing', 'bossEnrage', 'frailty', 'arenaPulses'] },
  { level: 9, name: 'Ascension IX', description: 'Second Wind is disabled.', modifiers: ['enemyDamage', 'noBossHealing', 'reinforcedWaves', 'fewerChoices', 'reducedPickupHealing', 'bossEnrage', 'frailty', 'arenaPulses', 'noRevive'] },
  { level: 10, name: 'Ascension X', description: 'Bosses begin with an extra attack layer.', modifiers: ['enemyDamage', 'noBossHealing', 'reinforcedWaves', 'fewerChoices', 'reducedPickupHealing', 'bossEnrage', 'frailty', 'arenaPulses', 'noRevive', 'extraBossLayer'] },
];

export function getAscension(level = 0) {
  return ASCENSIONS[Math.max(0, Math.min(10, level))];
}

export function createAscensionRules(level = 0) {
  const modifiers = getAscension(level).modifiers;
  const enemyStatMultiplier = getAscensionStatMultiplier(level);
  return {
    level,
    modifiers,
    // Enemy HP and damage share the same per-level stat climb (+20%/level,
    // capped at 3x). `enemyDamageMultiplier` is the damage-facing alias applied
    // to player.incomingDamageMultiplier; `enemyStatMultiplier` scales enemy HP.
    enemyStatMultiplier,
    enemyDamageMultiplier: enemyStatMultiplier,
    healBetweenBosses: !modifiers.includes('noBossHealing'),
    reinforcedWaves: modifiers.includes('reinforcedWaves'),
    levelUpChoices: modifiers.includes('fewerChoices') ? 2 : null,
    pickupsEnabled: true,
    pickupHealMultiplier: modifiers.includes('reducedPickupHealing') ? 0.5 : 1,
    bossEnrage: modifiers.includes('bossEnrage'),
    frailty: modifiers.includes('frailty'),
    arenaPulses: modifiers.includes('arenaPulses'),
    reviveEnabled: !modifiers.includes('noRevive'),
    extraBossLayer: modifiers.includes('extraBossLayer'),
  };
}
